import * as THREE from 'three';
import { createSeededRandom } from '../utils/random';
import { COURT, GOAL } from '../game/constants';

const cache = new Map<string, THREE.CanvasTexture>();

function makeCanvas(width: number, height: number): CanvasRenderingContext2D {
  const canvas = document.createElement('canvas');
  canvas.width = width;
  canvas.height = height;
  const context = canvas.getContext('2d');
  if (!context) throw new Error('Canvas 2D context unavailable');
  return context;
}

function finalize(
  context: CanvasRenderingContext2D,
  key: string,
  options: { repeat?: [number, number]; clamp?: boolean } = {},
): THREE.CanvasTexture {
  const texture = new THREE.CanvasTexture(context.canvas);
  texture.colorSpace = THREE.SRGBColorSpace;
  texture.anisotropy = 4;
  if (options.repeat) {
    texture.wrapS = THREE.RepeatWrapping;
    texture.wrapT = THREE.RepeatWrapping;
    texture.repeat.set(options.repeat[0], options.repeat[1]);
  } else if (options.clamp) {
    texture.wrapS = THREE.ClampToEdgeWrapping;
    texture.wrapT = THREE.ClampToEdgeWrapping;
  }
  cache.set(key, texture);
  return texture;
}

export function getCourtTexture(): THREE.CanvasTexture {
  const cached = cache.get('court');
  if (cached) return cached;
  const w = 2048;
  const h = 1216;
  const ctx = makeCanvas(w, h);
  const rng = createSeededRandom(7);
  const pxPerMeterX = w / (COURT.halfLength * 2);
  const pxPerMeterZ = h / (COURT.halfWidth * 2);
  const mx = (x: number) => (x + COURT.halfLength) * pxPerMeterX;
  const mz = (z: number) => (z + COURT.halfWidth) * pxPerMeterZ;

  const gradient = ctx.createLinearGradient(0, 0, w, h);
  gradient.addColorStop(0, '#286747');
  gradient.addColorStop(0.5, '#39855b');
  gradient.addColorStop(1, '#255f40');
  ctx.fillStyle = gradient;
  ctx.fillRect(0, 0, w, h);

  // Baked mowing bands and fine turf keep surface detail at one draw call.
  for (let stripe = 0; stripe < 12; stripe += 1) {
    ctx.fillStyle = stripe % 2 ? 'rgba(143,190,101,0.10)' : 'rgba(14,47,34,0.12)';
    ctx.fillRect(stripe * w / 12, 0, w / 12, h);
  }
  for (let i = 0; i < 32000; i += 1) {
    const x = rng() * w; const y = rng() * h;
    ctx.strokeStyle = rng() < 0.5 ? 'rgba(154,191,111,0.14)' : 'rgba(9,40,30,0.16)';
    ctx.beginPath(); ctx.moveTo(x, y); ctx.lineTo(x + 1, y - 2 - rng() * 3); ctx.stroke();
  }
  ctx.fillStyle = 'rgba(110,86,44,0.10)';
  for (const side of [-1, 1]) {
    ctx.beginPath(); ctx.ellipse(mx(side * 13.7), mz(0), 75, 95, 0, 0, Math.PI * 2); ctx.fill();
  }

  ctx.fillStyle = 'rgba(20, 60, 40, 0.35)';
  ctx.fillRect(0, 0, w, 26);
  ctx.fillRect(0, h - 26, w, 26);

  // Cleat wear follows the goalmouths and midfield traffic, avoiding polka-dot turf.
  for (const zone of [-1, 0, 1]) {
    for (let i = 0; i < 86; i += 1) {
      const x = mx(zone * 12.9) + (rng() - 0.5) * (zone ? 150 : 270);
      const y = mz(0) + (rng() - 0.5) * (zone ? 360 : 260);
      ctx.strokeStyle = rng() < 0.6 ? 'rgba(113,101,48,0.16)' : 'rgba(12,47,30,0.19)';
      ctx.lineWidth = 1 + rng() * 3;
      ctx.beginPath(); ctx.moveTo(x, y);
      ctx.lineTo(x + (rng() - 0.5) * 22, y + 3 + rng() * 12); ctx.stroke();
    }
  }
  for (let i = 0; i < 90; i += 1) {
    const x = rng() * w;
    const y = rng() * h;
    ctx.strokeStyle = `rgba(255,255,255,${0.015 + rng() * 0.03})`;
    ctx.lineWidth = 1 + rng() * 2;
    ctx.beginPath();
    ctx.moveTo(x, y);
    ctx.lineTo(x + (rng() - 0.5) * 90, y + (rng() - 0.5) * 40);
    ctx.stroke();
  }

  ctx.strokeStyle = 'rgba(245, 248, 240, 0.92)';
  ctx.lineWidth = 7;
  ctx.strokeRect(mx(-14.5), mz(-8.2), 29 * pxPerMeterX, 16.4 * pxPerMeterZ);

  ctx.lineWidth = 6;
  ctx.beginPath();
  ctx.moveTo(mx(0), mz(-8.2));
  ctx.lineTo(mx(0), mz(8.2));
  ctx.stroke();

  ctx.beginPath();
  ctx.ellipse(mx(0), mz(0), 2.6 * pxPerMeterX, 2.6 * pxPerMeterZ, 0, 0, Math.PI * 2);
  ctx.stroke();
  ctx.beginPath();
  ctx.arc(mx(0), mz(0), 0.22 * pxPerMeterX, 0, Math.PI * 2);
  ctx.fillStyle = 'rgba(245, 248, 240, 0.92)';
  ctx.fill();

  const areaDepth = 4.6;
  const areaHalfWidth = 3.6;
  for (const side of [-1, 1]) {
    const x0 = side < 0 ? mx(-14.5) : mx(14.5 - areaDepth);
    const width = areaDepth * pxPerMeterX;
    ctx.strokeRect(x0, mz(-areaHalfWidth), width, areaHalfWidth * 2 * pxPerMeterZ);
    ctx.beginPath();
    ctx.arc(mx(side * 10.2), mz(0), 0.22 * pxPerMeterX, 0, Math.PI * 2);
    ctx.fill();
    ctx.beginPath();
    ctx.arc(mx(side * 14.0), mz(0), 0.22 * pxPerMeterX, 0, Math.PI * 2);
    ctx.fill();

  }

  for (const xSide of [-1, 1]) for (const zSide of [-1, 1]) {
    ctx.beginPath();
    const start = xSide < 0 ? (zSide < 0 ? 0 : -Math.PI / 2) : (zSide < 0 ? Math.PI / 2 : Math.PI);
    ctx.ellipse(mx(xSide * 14.5), mz(zSide * 8.2), 0.65 * pxPerMeterX, 0.65 * pxPerMeterZ, 0, start, start + Math.PI / 2);
    ctx.stroke();
  }
  ctx.save();
  ctx.translate(mx(0), mz(0));
  ctx.rotate(-Math.PI / 2);
  ctx.font = `700 ${Math.round(1.15 * pxPerMeterX)}px "Arial Black", Arial, sans-serif`;
  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';
  ctx.fillStyle = 'rgba(255,255,255,0.10)';
  ctx.fillText('FÚTBOL FIGHTERS', 0, 0);
  ctx.restore();

  ctx.font = `800 ${Math.round(1.6 * pxPerMeterX)}px "Arial Black", Arial, sans-serif`;
  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';
  ctx.fillStyle = 'rgba(255,255,255,0.16)';
  ctx.fillText('A', mx(-12.6), mz(0));
  ctx.fillText('B', mx(12.6), mz(0));

  return finalize(ctx, 'court');
}

export function getAdTexture(): THREE.CanvasTexture {
  const cached = cache.get('ads');
  if (cached) return cached;
  // Match the seven wide boards without increasing the atlas pixel budget.
  const w = 4096;
  const h = 128;
  const ctx = makeCanvas(w, h);
  drawDefaultAds(ctx);
  return finalize(ctx, 'ads', { repeat: [1, 1] });
}

export const DEFAULT_AD_TEXTS = ['TURBO COLA', 'CHISPA ENERGY', 'DON PICANTE', 'ZAPATO VELOZ', 'GOL TV', 'MUEBLES TITÁN', 'PIZZA RAYO'] as const;
export const MAX_AD_TEXT_LENGTH = 120;
const AD_TAGLINES = [
  'SIN GAS NO HAY PATADAS',
  'RECARGA TU VENGANZA',
  'EL ARDOR NO LO CUBRE EL SEGURO',
  'CORRE. LA SILLA VIENE DETRÁS.',
  'REPETIMOS EL GOL. Y EL DELITO.',
  'SILLAS PARA SENTAR AL RIVAL',
  'LLEGAMOS ANTES QUE LA CAMILLA',
];

function drawAdCopy(ctx: CanvasRenderingContext2D, text: string, x: number, y: number, width: number, height: number, maxSize: number): void {
  let lines: string[] = [];
  let size = maxSize;
  for (; size >= 12; size -= 1) {
    ctx.font = `900 ${size}px "Arial Black", Arial, sans-serif`;
    lines = [''];
    for (const word of text.trim().split(/\s+/u)) {
      const last = lines.length - 1;
      const candidate = lines[last] ? `${lines[last]} ${word}` : word;
      if (ctx.measureText(candidate).width <= width) {
        lines[last] = candidate;
      } else {
        if (lines[last]) lines.push('');
        for (const char of Array.from(word)) {
          if (ctx.measureText(lines[lines.length - 1] + char).width > width) lines.push('');
          lines[lines.length - 1] += char;
        }
      }
    }
    if (lines.length <= 3 && lines.length * size * 1.12 <= height) break;
  }
  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';
  lines.forEach((line, i) => ctx.fillText(line, x, y + (i - (lines.length - 1) / 2) * size * 1.12));
}

function drawDefaultAds(ctx: CanvasRenderingContext2D): void {
  const w = ctx.canvas.width;
  const h = ctx.canvas.height;
  const rng = createSeededRandom(11);
  ctx.fillStyle = '#14181c';
  ctx.fillRect(0, 0, w, h);
  const colors = ['#ffd23f', '#ff5d5d', '#57c7ff', '#7dff8a', '#ff9de2', '#ffb347', '#b9a0ff'];
  const slot = w / DEFAULT_AD_TEXTS.length;
  for (let i = 0; i < DEFAULT_AD_TEXTS.length; i += 1) {
    const x = i * slot;
    ctx.fillStyle = i % 2 === 0 ? '#101317' : '#171c22';
    ctx.fillRect(x, 0, slot, h);
    ctx.fillStyle = colors[i];
    ctx.globalAlpha = 0.18;
    ctx.fillRect(x + 8, 12, slot - 16, h - 24);
    ctx.globalAlpha = 1;
    ctx.font = '900 38px "Arial Black", Arial, sans-serif';
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.fillStyle = colors[i];
    ctx.fillText(DEFAULT_AD_TEXTS[i], x + slot / 2, 36, slot - 32);
    ctx.fillStyle = '#fff8df';
    drawAdCopy(ctx, AD_TAGLINES[i], x + slot / 2, 88, slot - 40, 48, 25);
    ctx.fillStyle = colors[i];
    ctx.fillRect(x + 12, 5, slot - 24, 4);
    ctx.fillRect(x + 12, h - 9, slot - 24, 4);
    if (rng() > 0.4) {
      ctx.fillStyle = 'rgba(255,255,255,0.05)';
      ctx.fillRect(x, 0, 4, h);
    }
  }
}

export type AdvertisingDesign = { mode: 'default' } | { mode: 'text'; text: string } | { mode: 'image'; image: string };
let adImageRequest = 0;

export function setAdvertisingDesign(designs: readonly AdvertisingDesign[]): void {
  const texture = getAdTexture();
  const ctx = (texture.image as HTMLCanvasElement).getContext('2d');
  if (!ctx) return;
  const w = ctx.canvas.width;
  const h = ctx.canvas.height;
  const request = ++adImageRequest;
  drawDefaultAds(ctx);
  const slot = w / DEFAULT_AD_TEXTS.length;
  designs.slice(0, DEFAULT_AD_TEXTS.length).forEach((design, index) => {
    if (design.mode === 'default') return;
    const x = index * slot;
    if (design.mode === 'text') {
      const hue = (index * 51 + 18) % 360;
      ctx.fillStyle = '#121923';
      ctx.fillRect(x, 0, slot, h);
      ctx.fillStyle = `hsl(${hue} 54% 18%)`;
      ctx.fillRect(x + 5, 8, slot - 10, h - 16);
      ctx.strokeStyle = `hsl(${hue} 95% 62%)`;
      ctx.lineWidth = 6;
      ctx.strokeRect(x + 9, 12, slot - 18, h - 24);
      ctx.fillStyle = `hsl(${hue} 100% 72%)`;
      ctx.shadowColor = '#000';
      ctx.shadowBlur = 3;
      drawAdCopy(ctx, Array.from(design.text.trim()).slice(0, MAX_AD_TEXT_LENGTH).join('') || DEFAULT_AD_TEXTS[index], x + slot / 2, h / 2, slot - 40, h - 34, 44);
      ctx.shadowBlur = 0;
      return;
    }
    const image = new Image();
    image.onload = () => {
      if (request !== adImageRequest) return;
      ctx.fillStyle = '#121923';
      ctx.fillRect(x, 0, slot, h);
      const scale = Math.min(slot / image.width, h / image.height);
      const width = image.width * scale;
      const height = image.height * scale;
      ctx.save();
      ctx.beginPath();
      ctx.rect(x, 0, slot, h);
      ctx.clip();
      ctx.drawImage(image, x + (slot - width) / 2, (h - height) / 2, width, height);
      ctx.restore();
      ctx.strokeStyle = 'rgba(255,255,255,.8)';
      ctx.lineWidth = 4;
      ctx.strokeRect(x + 3, 3, slot - 6, h - 6);
      texture.needsUpdate = true;
    };
    image.src = design.image;
  });
  texture.needsUpdate = true;
}

export function getFenceTexture(): THREE.CanvasTexture {
  const cached = cache.get('fence');
  if (cached) return cached;
  const size = 256;
  const ctx = makeCanvas(size, size);
  ctx.clearRect(0, 0, size, size);
  ctx.strokeStyle = 'rgba(210, 220, 228, 0.85)';
  ctx.lineWidth = 1.5;
  const step = 32;
  for (let i = -size; i < size * 2; i += step) {
    ctx.beginPath();
    ctx.moveTo(i, 0);
    ctx.lineTo(i + size, size);
    ctx.stroke();
    ctx.beginPath();
    ctx.moveTo(i + size, 0);
    ctx.lineTo(i, size);
    ctx.stroke();
  }
  return finalize(ctx, 'fence', { repeat: [1, 1] });
}

export function getNetTexture(): THREE.CanvasTexture {
  const cached = cache.get('net');
  if (cached) return cached;
  const size = 128;
  const ctx = makeCanvas(size, size);
  ctx.clearRect(0, 0, size, size);
  ctx.strokeStyle = 'rgba(240, 245, 250, 0.9)';
  ctx.lineWidth = 2;
  const step = 16;
  for (let i = 0; i <= size; i += step) {
    ctx.beginPath();
    ctx.moveTo(i, 0);
    ctx.lineTo(i, size);
    ctx.stroke();
    ctx.beginPath();
    ctx.moveTo(0, i);
    ctx.lineTo(size, i);
    ctx.stroke();
  }
  return finalize(ctx, 'net', { repeat: [1, 1] });
}

export function getBallTexture(): THREE.CanvasTexture {
  const cached = cache.get('ball');
  if (cached) return cached;
  const size = 512;
  const ctx = makeCanvas(size, size);
  ctx.fillStyle = '#f6f5ef';
  ctx.fillRect(0, 0, size, size);
  ctx.fillStyle = '#17181c';
  const spots: Array<[number, number, number]> = [
    [0.5, 0.5, 0.16],
    [0.17, 0.2, 0.11],
    [0.83, 0.2, 0.11],
    [0.17, 0.8, 0.11],
    [0.83, 0.8, 0.11],
    [0.5, 0.03, 0.1],
    [0.5, 0.97, 0.1],
  ];
  for (const [sx, sy, sr] of spots) {
    ctx.beginPath();
    ctx.arc(sx * size, sy * size, sr * size, 0, Math.PI * 2);
    ctx.fill();
  }
  ctx.strokeStyle = 'rgba(30, 32, 38, 0.5)';
  ctx.lineWidth = 6;
  for (const [sx, sy, sr] of spots) {
    ctx.beginPath();
    ctx.arc(sx * size, sy * size, sr * size * 1.32, 0, Math.PI * 2);
    ctx.stroke();
  }
  ctx.fillStyle = '#e8483f';
  ctx.font = '800 60px "Arial Black", Arial, sans-serif';
  ctx.textAlign = 'center';
  ctx.fillText('F-B', size / 2, size * 0.34);
  return finalize(ctx, 'ball');
}

export function getCrateTexture(): THREE.CanvasTexture {
  const cached = cache.get('crate');
  if (cached) return cached;
  const size = 256;
  const ctx = makeCanvas(size, size);
  ctx.fillStyle = '#ffb52e';
  ctx.fillRect(0, 0, size, size);
  ctx.fillStyle = 'rgba(255,255,255,0.16)';
  ctx.fillRect(0, 0, size, 26);
  ctx.fillStyle = 'rgba(120, 60, 0, 0.28)';
  ctx.fillRect(0, size - 26, size, 26);
  ctx.strokeStyle = 'rgba(120, 60, 0, 0.5)';
  ctx.lineWidth = 10;
  ctx.strokeRect(5, 5, size - 10, size - 10);
  ctx.fillStyle = '#7a3c00';
  ctx.font = '900 150px "Arial Black", Arial, sans-serif';
  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';
  ctx.fillText('?', size / 2, size / 2 + 8);
  return finalize(ctx, 'crate');
}

export function getSkyTexture(): THREE.CanvasTexture {
  const cached = cache.get('sky');
  if (cached) return cached;
  const w = 512;
  const h = 512;
  const ctx = makeCanvas(w, h);
  const gradient = ctx.createLinearGradient(0, 0, 0, h);
  gradient.addColorStop(0, '#050916');
  gradient.addColorStop(0.45, '#0d1733');
  gradient.addColorStop(0.72, '#1d2f55');
  gradient.addColorStop(0.88, '#3a4c74');
  gradient.addColorStop(1, '#111827');
  ctx.fillStyle = gradient;
  ctx.fillRect(0, 0, w, h);
  const rng = createSeededRandom(23);
  for (let i = 0; i < 220; i += 1) {
    const x = rng() * w;
    const y = rng() * h * 0.7;
    const r = rng() * 1.6;
    ctx.fillStyle = `rgba(255,255,255,${0.25 + rng() * 0.6})`;
    ctx.beginPath();
    ctx.arc(x, y, r, 0, Math.PI * 2);
    ctx.fill();
  }
  const moonX = w * 0.72;
  const moonY = h * 0.2;
  const moonGlow = ctx.createRadialGradient(moonX, moonY, 4, moonX, moonY, 70);
  moonGlow.addColorStop(0, 'rgba(255, 250, 225, 0.9)');
  moonGlow.addColorStop(0.25, 'rgba(255, 250, 225, 0.35)');
  moonGlow.addColorStop(1, 'rgba(255, 250, 225, 0)');
  ctx.fillStyle = moonGlow;
  ctx.fillRect(moonX - 80, moonY - 80, 160, 160);
  ctx.fillStyle = '#fdf8e7';
  ctx.beginPath();
  ctx.arc(moonX, moonY, 22, 0, Math.PI * 2);
  ctx.fill();
  return finalize(ctx, 'sky', { clamp: true });
}

export interface FaceSpec {
  skin: string;
  hair: string;
  eyes: 'happy' | 'focused' | 'fierce';
  id: number;
}

export function getFaceTexture(spec: FaceSpec): THREE.CanvasTexture {
  const key = `face-${spec.id}`;
  const cached = cache.get(key);
  if (cached) return cached;
  const size = 128;
  const ctx = makeCanvas(size, size);
  ctx.clearRect(0, 0, size, size);

  const eyeY = 62;
  const eyeDX = 22;
  for (const side of [-1, 1]) {
    const ex = size / 2 + side * eyeDX;
    ctx.fillStyle = '#ffffff';
    ctx.beginPath();
    ctx.ellipse(ex, eyeY, 12, 14, 0, 0, Math.PI * 2);
    ctx.fill();
    ctx.fillStyle = '#26221c';
    const look = spec.eyes === 'focused' ? side * 2 : 0;
    ctx.beginPath();
    ctx.arc(ex + look, eyeY + 2, spec.eyes === 'fierce' ? 6.4 : 5.6, 0, Math.PI * 2);
    ctx.fill();
    ctx.fillStyle = '#ffffff';
    ctx.beginPath();
    ctx.arc(ex + 2 + look, eyeY - 1, 2.2, 0, Math.PI * 2);
    ctx.fill();
    ctx.strokeStyle = spec.hair;
    ctx.lineWidth = 7;
    ctx.lineCap = 'round';
    ctx.beginPath();
    if (spec.eyes === 'fierce') {
      ctx.moveTo(ex - 14, eyeY - 20);
      ctx.lineTo(ex + 12, eyeY - 12);
    } else if (spec.eyes === 'focused') {
      ctx.moveTo(ex - 13, eyeY - 16);
      ctx.lineTo(ex + 12, eyeY - 17);
    } else {
      ctx.moveTo(ex - 12, eyeY - 16);
      ctx.quadraticCurveTo(ex, eyeY - 23, ex + 12, eyeY - 15);
    }
    ctx.stroke();
  }

  ctx.strokeStyle = '#26221c';
  ctx.lineWidth = 4;
  ctx.lineCap = 'round';
  ctx.beginPath();
  if (spec.eyes === 'fierce') {
    ctx.moveTo(size / 2 - 12, 100);
    ctx.lineTo(size / 2 + 12, 98);
  } else {
    ctx.moveTo(size / 2 - 14, 94);
    ctx.quadraticCurveTo(size / 2, 108, size / 2 + 14, 94);
  }
  ctx.stroke();

  return finalize(ctx, key, { clamp: true });
}

export function getNumberTexture(number: number, color: string, id: number): THREE.CanvasTexture {
  const key = `number-${id}`;
  const cached = cache.get(key);
  if (cached) return cached;
  const size = 128;
  const ctx = makeCanvas(size, size);
  ctx.clearRect(0, 0, size, size);
  ctx.font = '900 92px "Arial Black", Arial, sans-serif';
  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';
  ctx.lineWidth = 8;
  ctx.strokeStyle = 'rgba(10, 12, 16, 0.85)';
  ctx.strokeText(String(number), size / 2, size / 2 + 4);
  ctx.fillStyle = color;
  ctx.fillText(String(number), size / 2, size / 2 + 4);
  return finalize(ctx, key, { clamp: true });
}

export function getStandsTexture(): THREE.CanvasTexture {
  const cached = cache.get('stands');
  if (cached) return cached;
  const w = 512;
  const h = 512;
  const ctx = makeCanvas(w, h);
  const rng = createSeededRandom(31);
  ctx.fillStyle = '#20242c';
  ctx.fillRect(0, 0, w, h);
  const rows = 14;
  const cols = 20;
  for (let row = 0; row < rows; row += 1) {
    const y = (row / rows) * h;
    ctx.fillStyle = row % 2 === 0 ? '#272c36' : '#222731';
    ctx.fillRect(0, y, w, h / rows);
    for (let col = 0; col < cols; col += 1) {
      if (rng() < 0.72) continue;
      const palette = ['#4b7dff', '#ff5d5d', '#ffd23f', '#57c7ff', '#ff9de2', '#8dffab'];
      ctx.fillStyle = palette[Math.floor(rng() * palette.length)];
      const x = (col / cols) * w + rng() * (w / cols) * 0.6;
      ctx.globalAlpha = 0.85;
      ctx.fillRect(x, y + 4, 10, h / rows * 0.55);
      ctx.globalAlpha = 1;
    }
  }
  return finalize(ctx, 'stands', { repeat: [1, 1] });
}

export function goalMouthX(side: -1 | 1): number {
  return side * COURT.halfLength;
}

export function goalPostPositions(): Array<[number, number, number]> {
  return [
    [-COURT.halfLength, GOAL.height / 2, -GOAL.halfWidth],
    [-COURT.halfLength, GOAL.height / 2, GOAL.halfWidth],
    [COURT.halfLength, GOAL.height / 2, -GOAL.halfWidth],
    [COURT.halfLength, GOAL.height / 2, GOAL.halfWidth],
  ];
}
